import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Heart, Plus, Pencil, Trash2, Loader2, ChevronLeft, Search,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { donationsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GlassCard, PageLoading } from '../components/shared';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import RichTextEditor from '../components/admin/RichTextEditor';
import { toast } from 'sonner';

const CATEGORIES = ['orphans', 'mosque', 'education', 'dawah', 'emergency', 'other', 'app_support'];
const QUEST_STATUSES = ['active', 'paused', 'completed', 'archived'];

const fmtAmount = (cents) =>
  cents != null ? `${(Number(cents) / 100).toFixed(2)} €` : '—';

const QuestForm = ({ initial, onCancel, onSaved }) => {
  const isEdit = !!initial;
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(() => ({
    title_fr: initial?.title_fr || '',
    title_ar: initial?.title_ar || '',
    slug: initial?.slug || '',
    description_html: initial?.description_html || '',
    image_url: initial?.image_url || '',
    category: initial?.category || 'other',
    goal_amount_eur: initial?.goal_amount_cents ? (initial.goal_amount_cents / 100).toString() : '',
    status: initial?.status || 'active',
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = {
        title_fr: form.title_fr,
        title_ar: form.title_ar || null,
        slug: form.slug || null,
        description_html: form.description_html,
        image_url: form.image_url || null,
        category: form.category,
        goal_amount_cents: Math.round(parseFloat(form.goal_amount_eur || '0') * 100) || 0,
        currency: 'EUR',
        status: form.status,
      };
      if (isEdit) {
        await donationsApi.admin.update(initial.id, body);
        toast.success('Quête mise à jour');
      } else {
        await donationsApi.admin.create(body);
        toast.success('Quête créée');
      }
      onSaved?.();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      const detail = data?.detail ?? data?.error;
      const detailText = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join(' ; ')
        : (typeof detail === 'string' ? detail : detail?.msg);
      let msg;
      if (err?.code === 'ERR_NETWORK' || !err?.response) {
        // Distinguish a "real" network failure (no response at all) from a
        // backend 5xx that simply lost its CORS headers in transit.
        msg = 'Le serveur est injoignable (réseau ou CORS). Vérifie ta connexion et la configuration backend.';
      } else if (status === 409) {
        msg = detailText || 'Slug déjà utilisé.';
      } else if (status === 422) {
        msg = detailText || 'Données invalides — vérifie les champs obligatoires.';
      } else if (status >= 500) {
        msg = `Erreur serveur (${status}) — ${detailText || 'voir la console pour le détail.'}`;
      } else {
        msg = detailText || err?.message || 'Erreur inconnue';
        if (status) msg = `[${status}] ${msg}`;
      }
      toast.error(`Création impossible : ${msg}`);
      // eslint-disable-next-line no-console
      console.error('Quest create error:', data || err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Titre (FR) *</Label>
          <Input value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} required className="bg-surface border-border" data-testid="quest-form-title-fr" />
        </div>
        <div>
          <Label>Titre (AR)</Label>
          <Input value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} dir="rtl" className="bg-surface border-border" data-testid="quest-form-title-ar" />
        </div>
        <div>
          <Label>Slug (laisser vide = auto)</Label>
          <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="ex. aide-orphelins" className="bg-surface border-border font-mono" />
        </div>
        <div>
          <Label>URL d'image</Label>
          <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" className="bg-surface border-border" />
        </div>
        <div>
          <Label>Catégorie *</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="bg-surface border-border" data-testid="quest-form-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Statut *</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger className="bg-surface border-border" data-testid="quest-form-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {QUEST_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label>Objectif (€)</Label>
          <Input
            type="number"
            min={0}
            step="1"
            value={form.goal_amount_eur}
            onChange={(e) => setForm({ ...form, goal_amount_eur: e.target.value })}
            placeholder="ex. 5000"
            className="bg-surface border-border"
            data-testid="quest-form-goal"
          />
        </div>
      </div>
      <div>
        <Label>Description</Label>
        <RichTextEditor
          testid="quest-form-description-editor"
          value={form.description_html}
          onChange={(html) => setForm({ ...form, description_html: html })}
          placeholder="Décrivez la quête, son contexte, ses objectifs concrets…"
        />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>Annuler</Button>
        <Button type="submit" disabled={loading} className="bg-primary text-background" data-testid="quest-form-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {isEdit ? 'Mettre à jour' : 'Créer la quête'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const AdminQuests = () => {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing] = useState(undefined);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter, limit: 200 } : { limit: 200 };
      const r = await donationsApi.admin.list(params);
      setItems(r.data?.items || []);
    } catch {
      toast.error('Erreur de chargement');
      setItems([]);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (user?.role === 'admin') fetchAll();
  }, [user, fetchAll]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const r = await donationsApi.admin.remove(confirmDelete.id);
      toast.success(r.data?.action === 'archived' ? 'Quête archivée (dons existants conservés)' : 'Quête supprimée');
      setConfirmDelete(null);
      await fetchAll();
    } catch (e) {
      toast.error('Échec de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading) return <PageLoading />;
  if (!user || user.role !== 'admin') return <Navigate to="/login" replace />;

  const q = search.trim().toLowerCase();
  const filtered = items === null
    ? null
    : items.filter((it) => {
        if (!q) return true;
        const hay = [it.title_fr, it.title_ar, it.slug, it.category].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <div>
            <Link to="/admin" className="inline-flex items-center text-sm text-text-muted hover:text-primary mb-2" data-testid="admin-quests-back">
              <ChevronLeft className="w-4 h-4 mr-1" />Retour au tableau de bord
            </Link>
            <h1 className="text-3xl font-serif text-text-primary flex items-center gap-2">
              <Heart className="w-7 h-7 text-rose-400" />Gestion des quêtes (dons)
            </h1>
            <p className="text-text-secondary mt-1">{items?.length ?? '…'} quête(s)</p>
          </div>
          <Button className="bg-primary text-background" onClick={() => setEditing(null)} data-testid="admin-quests-add-btn">
            <Plus className="w-4 h-4 mr-1" />Créer une quête
          </Button>
        </div>

        <GlassCard className="mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <Input
                placeholder="Rechercher (titre, slug, catégorie)…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-surface border-border"
                data-testid="admin-quests-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-surface border-border" data-testid="admin-quests-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {QUEST_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </GlassCard>

        {items === null ? <PageLoading /> : filtered.length === 0 ? (
          <GlassCard className="text-center py-12 text-text-muted">Aucune quête.</GlassCard>
        ) : (
          <div className="space-y-3" data-testid="admin-quests-list">
            {filtered.map((q) => (
              <GlassCard key={q.id} className="flex items-start justify-between gap-3 flex-wrap" data-testid={`admin-quest-row-${q.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-surface-elevated text-text-muted border border-border">{q.status}</span>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-surface-elevated text-primary border border-primary/30">{q.category}</span>
                    <h3 className="font-serif text-lg text-text-primary">{q.title_fr}</h3>
                    {q.title_ar && <span className="font-arabic text-base text-primary" dir="rtl">{q.title_ar}</span>}
                  </div>
                  <div className="text-xs text-text-muted font-mono mt-1">/{q.slug}</div>
                  <div className="text-xs text-text-muted mt-2">
                    {fmtAmount(q.raised_amount_cents)} collectés / {fmtAmount(q.goal_amount_cents)} d'objectif
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => setEditing(q)} data-testid={`admin-quest-edit-${q.id}`}>
                    <Pencil className="w-4 h-4 mr-1" />Modifier
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10"
                    onClick={() => setConfirmDelete(q)}
                    data-testid={`admin-quest-delete-${q.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </GlassCard>
            ))}
          </div>
        )}

        {/* Form dialog */}
        <Dialog open={editing !== undefined} onOpenChange={(v) => { if (!v) setEditing(undefined); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-quest-form-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? `Modifier : ${editing.title_fr}` : 'Créer une quête'}</DialogTitle>
              <DialogDescription>Champs marqués d'une * obligatoires. La description supporte le rich-text.</DialogDescription>
            </DialogHeader>
            {editing !== undefined && (
              <QuestForm
                initial={editing}
                onCancel={() => setEditing(undefined)}
                onSaved={async () => {
                  setEditing(undefined);
                  await fetchAll();
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Delete confirm */}
        <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
          <AlertDialogContent data-testid="admin-quest-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cette quête ?</AlertDialogTitle>
              <AlertDialogDescription>
                Si la quête a déjà reçu des dons, elle sera <strong>archivée</strong> (dons conservés pour traçabilité).
                Sinon elle sera supprimée définitivement.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="admin-quest-delete-confirm-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Confirmer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default AdminQuests;
